import React, { useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useAnimation } from 'framer-motion';
import { FiShield, FiHeart, FiUsers, FiLock, FiCompass, FiGlobe } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const WhyUs = () => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, threshold: 0.1 });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const features = [
    {
      icon: FiShield,
      title: "Verified Safe Spaces",
      description: "Every business and event is verified for inclusivity, ensuring authentic queer-friendly experiences."
    },
    {
      icon: FiHeart,
      title: "Community-Driven",
      description: "Built by and for the LGBTQIA+ community, with your input shaping every feature and update."
    },
    {
      icon: FiUsers,
      title: "Instant Connection",
      description: "Connect with businesses and creatives who share your values and celebrate your identity."
    },
    {
      icon: FiLock,
      title: "Privacy First",
      description: "Your personal information stays private. We never sell data or share without explicit consent."
    },
    {
      icon: FiCompass,
      title: "Local Discovery",
      description: "Find what's happening near you, from pop-up markets to long-standing community pillars."
    },
    {
      icon: FiGlobe,
      title: "Global Impact",
      description: "Support local businesses while contributing to a global movement for equality and acceptance."
    }
  ];

  return (
    <section id="why-us" className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Everything You Need in One Place
          </motion.h2>
          <motion.p 
            variants={itemVariants}
            className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto"
          >
            Say goodbye to endless Google searches. Togethr simplifies how you discover, connect, and engage with LGBTQIA+ events and businesses in your community.
          </motion.p>
        </motion.div>

        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-purple-100"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
                <SafeIcon icon={feature.icon} className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUs;